import { Injectable } from '@angular/core'
import { ForceAny, RequiredBy } from '@shared/types'
import { Subject, filter, of } from 'rxjs'

export interface TabCommMessage<T = ForceAny> {
  key: string
  payload: T
  source: 'main' | 'child'
}

export interface TabRouteOptions {
  route: string
  origin?: string
  /** just change the url without focusing if the tab is already opened */
  noFocusIfOpened?: boolean
  /** it will not open new tab but change the route if already opened */
  changeIfOpened?: boolean
  key?: string // unique identifier per tab type
}

@Injectable({
  providedIn: 'root',
})
export class TabCommService<TPayload = ForceAny> {
  private key: string | undefined

  private openTabs: Record<string, Window> = {}
  private listeners: Record<string, Subject<TabCommMessage<TPayload>>> = {}
  private readonly defaultOrigin = window.location.origin

  constructor() {
    window.addEventListener('message', this.receiveMessage.bind(this))
  }

  getKey(key?: string) {
    return key || this.key
  }

  initKey(key: string) {
    this.key = key
  }

  restoreWindowsFromSession(key = this.key) {
    const keys = Object.keys(sessionStorage).filter((k) => k === `TAB_REF_${key}`)

    keys.forEach((k) => {
      const winName = sessionStorage.getItem(k)
      if (winName) {
        const winRef = window.open('', winName)
        if (winRef) {
          const newKey = k.replace('TAB_REF_', '')
          this.openTabs[newKey] = winRef
        }
      }
    })
  }

  buildUrl(route: string, origin: string = this.defaultOrigin): string {
    try {
      return new URL(route, origin).toString()
    } catch (e) {
      console.error('Invalid URL in buildUrl:', { route, origin })
      throw e
    }
  }

  openTab(options: TabRouteOptions): Window | null {
    const { key, route, origin, noFocusIfOpened } = { key: this.key, ...options } as RequiredBy<TabRouteOptions, 'key'>
    const finalUrl = this.buildUrl(route, origin)

    const existingWin = this.openTabs[key]
    if (existingWin && !existingWin.closed) {
      // Tab is already open — just update its location if needed
      if (!existingWin.location.href.includes(finalUrl)) {
        existingWin.location.href = finalUrl
      }
      if (!noFocusIfOpened) existingWin.focus()
      return existingWin
    }

    if (options.changeIfOpened) {
      return null
    }

    const windowName = `TAB_${key}`
    const newTab = window.open(finalUrl, windowName)
    if (newTab) {
      this.openTabs[key] = newTab
      sessionStorage.setItem(`TAB_REF_${key}`, windowName)
    }

    return newTab
  }

  sendMessage(payload: TPayload, key = this.key) {
    if (!key) {
      console.error('No key provided')
      return
    }
    const win = this.openTabs[key]
    if (win && !win.closed) {
      win.postMessage({ key, payload, source: 'main' } as TabCommMessage<TPayload>, '*')
    }
  }

  onMessage(key = this.key) {
    if (!key) {
      console.error('No key provided')
      return of({ key, payload: null, source: 'main' } as TabCommMessage<TPayload>)
    }

    if (!this.listeners[key]) {
      this.listeners[key] = new Subject<TabCommMessage<TPayload>>()
    }

    return this.listeners[key].asObservable().pipe(filter((msg) => msg.key === key))
  }

  receiveMessage(event: MessageEvent) {
    const data = event.data as TabCommMessage<TPayload>
    if (data?.key && this.listeners[data.key]) {
      this.listeners[data.key].next(data)
    }
  }
}
